import { buildCdnPath } from "./buildCdnPath";
import { buildCdnRenderPath } from "./buildCdnRenderPath";
import { buildCdnSrcSet } from "./buildCdnSrcSet";

type ImageId = number | string;
export const imageRenderWidths = [150, 320, 480, 640, 960, 1280, 1600] as const;
export type ImageRenderWidth = (typeof imageRenderWidths)[number];
export type ImageRenderFormat = "avif" | "jpeg" | "png" | "webp";

type SizeOption = { size?: "thumb150" | "full" };
export type ImageRenderOption = SizeOption & {
  format?: ImageRenderFormat;
  width?: ImageRenderWidth;
};

function renderWidth(options?: ImageRenderOption): ImageRenderWidth | undefined {
  return options?.width ?? (options?.size === "thumb150" ? 150 : undefined);
}

function withImageRenderOptions(path: string, options?: ImageRenderOption): string {
  const width = renderWidth(options);
  if (!width) return path;
  return buildCdnRenderPath(path, {
    width,
    fit: "inside",
    position: "center",
    format: options?.format ?? "webp",
  });
}

export function withImageRenderParams(imageUrl: string, options?: ImageRenderOption): string;
export function withImageRenderParams(
  imageUrl: string | null | undefined,
  options?: ImageRenderOption,
): string | null;
export function withImageRenderParams(
  imageUrl: string | null | undefined,
  options?: ImageRenderOption,
): string | null {
  return imageUrl == null ? null : withImageRenderOptions(imageUrl, options);
}

export function buildImageSrcSet(
  imageUrl: string,
  widths: readonly ImageRenderWidth[],
  options?: Omit<ImageRenderOption, "size" | "width">,
): string;
export function buildImageSrcSet(
  imageUrl: string | null | undefined,
  widths: readonly ImageRenderWidth[],
  options?: Omit<ImageRenderOption, "size" | "width">,
): string | undefined;
export function buildImageSrcSet(
  imageUrl: string | null | undefined,
  widths: readonly ImageRenderWidth[],
  options?: Omit<ImageRenderOption, "size" | "width">,
): string | undefined {
  if (imageUrl == null) return undefined;
  return buildCdnSrcSet(imageUrl, widths, {
    ...options,
    fit: "inside",
    position: "center",
    format: options?.format ?? "webp",
  });
}

function buildEntityImageSrcSet(
  path: string | null,
  widths: readonly ImageRenderWidth[],
  options?: Omit<ImageRenderOption, "size" | "width">,
): string | undefined {
  return path ? buildImageSrcSet(path, widths, options) : undefined;
}

/** Build proxy URL for artist image. Returns null when id is null/undefined. */
export function buildArtistImageUrl(artistId: ImageId): string;
export function buildArtistImageUrl(artistId: ImageId | null | undefined): string | null;
export function buildArtistImageUrl(artistId: ImageId | null | undefined): string | null {
  return artistId == null ? null : buildCdnPath(["artist", artistId]);
}

/** Build proxy URL for album (work) cover image. */
export function buildAlbumImageUrl(albumId: ImageId, options?: ImageRenderOption): string;
export function buildAlbumImageUrl(
  albumId: ImageId | null | undefined,
  options?: ImageRenderOption,
): string | null;
export function buildAlbumImageUrl(
  albumId: ImageId | null | undefined,
  options?: ImageRenderOption,
): string | null {
  return albumId == null ? null : withImageRenderOptions(buildCdnPath(["album", albumId]), options);
}

export function buildAlbumImageSrcSet(
  albumId: ImageId | null | undefined,
  widths: readonly ImageRenderWidth[],
  options?: Omit<ImageRenderOption, "size" | "width">,
): string | undefined {
  return buildEntityImageSrcSet(buildAlbumImageUrl(albumId), widths, options);
}

/** Build proxy URL for release-specific cover image. */
export function buildReleaseImageUrl(releaseId: ImageId, options?: ImageRenderOption): string;
export function buildReleaseImageUrl(
  releaseId: ImageId | null | undefined,
  options?: ImageRenderOption,
): string | null;
export function buildReleaseImageUrl(
  releaseId: ImageId | null | undefined,
  options?: ImageRenderOption,
): string | null {
  return releaseId == null
    ? null
    : withImageRenderOptions(buildCdnPath(["release", releaseId, 0]), options);
}

export function buildReleaseImageSrcSet(
  releaseId: ImageId | null | undefined,
  widths: readonly ImageRenderWidth[],
  options?: Omit<ImageRenderOption, "size" | "width">,
): string | undefined {
  return buildEntityImageSrcSet(buildReleaseImageUrl(releaseId), widths, options);
}

/** Build proxy URL for a specific release gallery image. */
export function buildReleaseGalleryImageUrl(
  releaseId: ImageId,
  imageIndex: number,
  options?: ImageRenderOption,
): string;
export function buildReleaseGalleryImageUrl(
  releaseId: ImageId | null | undefined,
  imageIndex: number,
  options?: ImageRenderOption,
): string | null;
export function buildReleaseGalleryImageUrl(
  releaseId: ImageId | null | undefined,
  imageIndex: number,
  options?: ImageRenderOption,
): string | null {
  return releaseId == null
    ? null
    : withImageRenderOptions(buildCdnPath(["release", releaseId, imageIndex]), options);
}

export function buildReleaseGalleryImageSrcSet(
  releaseId: ImageId | null | undefined,
  imageIndex: number,
  widths: readonly ImageRenderWidth[],
  options?: Omit<ImageRenderOption, "size" | "width">,
): string | undefined {
  return buildEntityImageSrcSet(
    buildReleaseGalleryImageUrl(releaseId, imageIndex),
    widths,
    options,
  );
}

/** Build proxy URL for a tracked marketplace offer image. */
export function buildOfferImageUrl(offerId: ImageId, options?: ImageRenderOption): string;
export function buildOfferImageUrl(
  offerId: ImageId | null | undefined,
  options?: ImageRenderOption,
): string | null;
export function buildOfferImageUrl(
  offerId: ImageId | null | undefined,
  options?: ImageRenderOption,
): string | null {
  return offerId == null ? null : withImageRenderOptions(buildCdnPath(["offer", offerId]), options);
}

export function buildOfferImageSrcSet(
  offerId: ImageId | null | undefined,
  widths: readonly ImageRenderWidth[],
  options?: Omit<ImageRenderOption, "size" | "width">,
): string | undefined {
  return buildEntityImageSrcSet(buildOfferImageUrl(offerId), widths, options);
}

/** Build proxy URL for artist background image. */
export function buildArtistBackgroundUrl(artistId: ImageId): string;
export function buildArtistBackgroundUrl(artistId: ImageId | null | undefined): string | null;
export function buildArtistBackgroundUrl(artistId: ImageId | null | undefined): string | null {
  return artistId == null ? null : buildCdnPath(["artist", artistId, "background"]);
}

/** Build proxy URL for label image. */
export function buildLabelImageUrl(labelId: ImageId): string;
export function buildLabelImageUrl(labelId: ImageId | null | undefined): string | null;
export function buildLabelImageUrl(labelId: ImageId | null | undefined): string | null {
  return labelId == null ? null : buildCdnPath(["company", labelId]);
}

/** Build proxy URL for company image. */
export function buildCompanyImageUrl(companyId: ImageId): string;
export function buildCompanyImageUrl(companyId: ImageId | null | undefined): string | null;
export function buildCompanyImageUrl(companyId: ImageId | null | undefined): string | null {
  return companyId == null ? null : buildCdnPath(["company", companyId]);
}

export const HOME_COVER_SECTIONS = ["hero", "features", "final"] as const;
export type HomeCoverSection = (typeof HOME_COVER_SECTIONS)[number];

const HOME_COVER_SOURCE_VERSION = "v2";

/** Build a separately cached proxy URL for a home-page section background. */
export function buildHomeCoverUrl(section: HomeCoverSection): string {
  return buildCdnPath(["home", `${section}-${HOME_COVER_SOURCE_VERSION}`]);
}
